import { useState, useEffect } from "react";
import {
  Menu, X, ChevronDown, ChevronUp, Check,
  ArrowRight, Phone, MapPin, Mail, Instagram, Facebook, Youtube,
  Wrench, MessageCircle, Search,
  Layers, Droplets, CheckCircle, AlertTriangle,
  Volume2, Palette, Compass, Hammer,
} from "lucide-react";
import { trackEvent } from "../utils/analytics";

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const navy = {
  deepest: "#030E1A",
  base:    "#061B2D",
  mid:     "#08233A",
  light:   "#0A2A43",
  subtle:  "#0D3252",
};
const orange = {
  base:   "#D95F0E",
  hover:  "#C9540A",
  light:  "#E46A12",
  tint:   "rgba(217,95,14,0.10)",
  tintLg: "rgba(217,95,14,0.18)",
};

const NAV_LINKS = [
  { label: "Home",           href: "#top" },
  { label: "Services",       href: "#services" },
  { label: "Process",        href: "#process" },
  { label: "Floor Assessment", href: "#assessment" },
  { label: "FAQ",            href: "#faq" },
  { label: "Contact",        href: "#contact" },
];

// ─── Header ───────────────────────────────────────────────────────────────────
function Header({ scrolled }: { scrolled: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: navy.base,
        boxShadow: scrolled ? "0 2px 20px rgba(3,14,26,0.45)" : "none",
        paddingTop:    scrolled ? "12px" : "18px",
        paddingBottom: scrolled ? "12px" : "18px",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#top" aria-label="VC Flooring home" className="flex items-center">
          <img
            src="/assets/vc-flooring-logo-white.png"
            alt="VC Flooring"
            width="616"
            height="392"
            className="h-12 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary navigation">
          {NAV_LINKS.map((link, i) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm font-medium transition-colors"
              style={{ color: i === 0 ? orange.light : "rgba(255,255,255,0.80)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = orange.light)}
              onMouseLeave={(e) => (e.currentTarget.style.color = i === 0 ? orange.light : "rgba(255,255,255,0.80)")}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href="#contact"
            className="px-5 py-2.5 text-white text-sm font-semibold rounded-sm transition-colors"
            style={{ background: orange.base }}
            onMouseEnter={(e) => (e.currentTarget.style.background = orange.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = orange.base)}
          >
            Request an On-Site Estimate
          </a>
        </div>

        <button
          className="lg:hidden text-white min-w-11 min-h-11 flex items-center justify-center"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <nav id="mobile-navigation" className="lg:hidden border-t px-6 py-4" aria-label="Mobile navigation" style={{ background: navy.mid, borderColor: "rgba(255,255,255,0.08)" }}>
          {NAV_LINKS.map((link) => (
            <a key={link.label} href={link.href} className="block py-3 text-sm font-medium text-white/75 border-b transition-colors hover:text-white" style={{ borderColor: "rgba(255,255,255,0.07)" }} onClick={() => setMobileOpen(false)}>
              {link.label}
            </a>
          ))}
          <a href="#contact" className="mt-4 block w-full text-center px-5 py-3 text-white font-semibold rounded-sm" style={{ background: orange.base }}>
            Request an On-Site Estimate
          </a>
        </nav>
      )}
    </header>
  );
}
// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  const credentials = [
    "Direct Communication",
    "Clear Project Scope",
    "Professional Preparation",
    "Final Walkthrough",
  ];

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0" style={{ background: navy.base }}>
        <img
          src="/assets/hero.jpg"
          alt="Modern home interior with beautiful hardwood flooring"
          width="1920"
          height="1080"
          className="w-full h-full object-cover"
          style={{ opacity: 0.28 }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${navy.base}E8 0%, ${navy.mid}C0 50%, ${navy.base}70 100%)` }} />
        <div className="absolute bottom-0 left-0 right-0 h-32" style={{ background: `linear-gradient(to top, ${navy.base}, transparent)` }} />
      </div>
      <div className="absolute left-0 top-0 w-1 h-full" style={{ background: `linear-gradient(to bottom, ${orange.base}, transparent)` }} />

      <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-8 border" style={{ background: orange.tint, borderColor: `${orange.base}55` }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: orange.base }} />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.light }}>Residential Flooring · Colorado</span>
          </div>

          <h1 className="text-5xl lg:text-6xl xl:text-[4.5rem] font-semibold text-white leading-[1.06] mb-7" style={{ fontFamily: "Fraunces, serif" }}>
            Understand Your Options.{" "}
            <em className="not-italic" style={{ color: orange.light }}>Choose the Right Solution.</em>
          </h1>

          <p className="text-white/65 text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl">
            Expert guidance, installation, refinishing and repair for hardwood, vinyl and laminate floors — with clear recommendations from the first assessment to the final walkthrough.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <a href="#contact" onClick={() => trackEvent("cta_click", { label: "Request an On-Site Estimate", placement: "hero" })} className="px-9 py-4 text-white font-semibold text-base rounded-sm transition-colors text-center" style={{ background: orange.base }} onMouseEnter={(e) => (e.currentTarget.style.background = orange.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = orange.base)}>
              Request an On-Site Estimate
            </a>
            <a href="#services" className="px-9 py-4 border text-white font-medium text-base rounded-sm transition-all flex items-center justify-center gap-2 hover:bg-white/8" style={{ borderColor: "rgba(255,255,255,0.35)" }}>
              Explore Our Services <ArrowRight size={16} />
            </a>
          </div>

          {/* ── Credential bar ── */}
          <div className="border-t pt-8" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
            <div className="flex flex-wrap gap-x-6 gap-y-4">
              {credentials.map((label, i) => (
                <div key={label} className="flex items-center gap-2.5">
                  {i > 0 && <div className="hidden sm:block w-px h-6 self-center" style={{ background: "rgba(255,255,255,0.12)" }} />}
                  <Check size={13} style={{ color: orange.light }} />
                  <div className="text-[10px] text-white/55 uppercase tracking-wider leading-snug">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-35">
        <span className="text-white text-[10px] tracking-[0.2em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white to-transparent" />
      </div>
    </section>
  );
}

// ─── Trust Strip ──────────────────────────────────────────────────────────────
function TrustStrip() {
  const items = ["Direct Communication", "Clear Project Scope", "Professional Preparation", "Final Walkthrough"];
  return (
    <div className="py-4 border-y overflow-hidden" style={{ background: navy.mid, borderColor: "rgba(255,255,255,0.06)" }}>
      <div className="max-w-7xl mx-auto px-6 flex flex-wrap lg:flex-nowrap items-center justify-between gap-4">
        {items.map((item, i) => (
          <div key={item} className="flex items-center gap-2.5 text-sm text-white/55">
            {i > 0 && <div className="hidden lg:block w-1 h-1 rounded-full" style={{ background: orange.base }} />}
            <Check size={12} style={{ color: orange.base }} />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Why Victor ───────────────────────────────────────────────────────────────
function WhyVictorSection() {
  const benefits = [
    { icon: Search, title: "Understand Before Recommending", desc: "We begin with your current floor, your concerns and the result you want to achieve." },
    { icon: Compass, title: "Clear Options", desc: "Understand the benefits, limitations, process and investment behind each possible solution." },
    { icon: Wrench, title: "Prepared Execution", desc: "Materials, subfloor conditions, moisture, transitions and manufacturer requirements are considered before work begins." },
    { icon: CheckCircle, title: "Present After Completion", desc: "The project ends with a final walkthrough, clear care guidance and continued availability when you need help." },
  ];

  return (
    <section className="bg-white py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left — image column */}
          <div className="lg:col-span-5">
            <div className="relative sticky top-28">
              <div className="aspect-[3/4] rounded-sm overflow-hidden bg-[#E4EAF0]">
                <img
                  src="/assets/victor-installing.jpg"
                  alt="Victor installing hardwood flooring"
                  width="900"
                  height="1200"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Right — content column */}
          <div className="lg:col-span-7 pt-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-6" style={{ background: orange.tint }}>
              <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.base }}>A Consultative Approach</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>
              Flooring Guidance That Starts With Your Home
            </h2>

            {/* Personal intro */}
            <p className="text-[#374151] text-lg leading-relaxed mb-3">
              Every flooring project begins with a different situation. You may be dealing with damage, worn hardwood, an uneven subfloor, a home renovation or simply uncertainty about which option makes sense.
            </p>
            <p className="text-[#64748B] text-base leading-relaxed mb-3">
              Victor works directly with homeowners to evaluate the current floor, understand the desired result and explain the available paths. Sometimes the right solution is a repair. Sometimes it is refinishing. And sometimes replacement is the better long-term investment.
            </p>
            <p className="text-[#64748B] text-base leading-relaxed mb-10">
              The goal is not to recommend the most expensive service. It is to help you make a well-informed decision for your home, routine and budget.
            </p>

            {/* Four value cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {benefits.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="p-5 bg-white rounded-sm transition-all group cursor-default border hover:shadow-md"
                  style={{ borderColor: "#E4EAF0" }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = `${orange.base}50`)}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = "#E4EAF0")}
                >
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center mb-3" style={{ background: orange.tint }}>
                    <Icon size={18} style={{ color: orange.base }} />
                  </div>
                  <h3 className="font-semibold mb-2" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>{title}</h3>
                  <p className="text-[#64748B] text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <a
              href="#contact"
              className="inline-flex items-center gap-3 px-8 py-4 text-white font-semibold rounded-sm transition-colors"
              style={{ background: navy.base }}
              onMouseEnter={(e) => (e.currentTarget.style.background = navy.light)}
              onMouseLeave={(e) => (e.currentTarget.style.background = navy.base)}
            >
              Talk Directly With Victor <ArrowRight size={15} />
            </a>

            {/* Signature */}
            <div className="mt-10 pt-8 border-t flex items-center gap-4" style={{ borderColor: "#E4EAF0" }}>
              <div>
                <div className="text-2xl font-semibold italic leading-none mb-1" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>
                  Victor Barbosa
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">Flooring Specialist &amp; Consultant · Denver, CO</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Flooring Types ───────────────────────────────────────────────────────────
function FlooringTypesSection() {
  const groups = [
    {
      groupLabel: "Installation & Renewal",
      groupDesc:  "Solutions planned around the condition of your home",
      cards: [
        {
          title: "Floor Installation",
          desc:  "Professional installation of hardwood, engineered hardwood, LVP and laminate, with careful subfloor preparation, layout, transitions and finishing details.",
          img:   "/assets/floor-vinyl.jpg",
          alt:   "Flooring installation detail",
        },
        {
          title: "Hardwood Refinishing",
          desc:  "Sanding, surface preparation, stain and finish systems designed to restore the appearance of existing hardwood and protect it for years to come.",
          img:   "/assets/floor-laminate.jpg",
          alt:   "Refinished hardwood floor in a modern room",
        },
      ],
    },
    {
      groupLabel: "Repair & Maintenance",
      groupDesc:  "Targeted work when full replacement may not be necessary",
      cards: [
        {
          title: "Floor Repairs",
          desc:  "Localized solutions for damaged boards, scratches, stains, water-related issues, transitions and other problems that may not require full replacement.",
          img:   "/assets/floor-engineered.jpg",
          alt:   "Hardwood floor repair detail",
        },
        {
          title: "Screen & Recoat",
          desc:  "Renew the protective finish and appearance of eligible hardwood floors without the complete sanding process required for full refinishing.",
          img:   "/assets/floor-solid.jpg",
          alt:   "Hardwood floor with a renewed protective finish",
        },
      ],
    },
  ];

  return (
    <section id="services" className="py-24 lg:py-32" style={{ background: navy.base }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-4 border" style={{ background: orange.tintLg, borderColor: `${orange.base}40` }}>
              <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.light }}>Flooring Services</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-white leading-tight" style={{ fontFamily: "Fraunces, serif" }}>The Right Service Starts With the Right Diagnosis</h2>
          </div>
          <p className="text-white/40 text-lg max-w-sm lg:text-right">
            Not every damaged or outdated floor needs to be replaced. We assess the condition, your goals and the possibilities before recommending a path.
          </p>
        </div>

        {/* Two groups */}
        <div className="space-y-12">
          {groups.map(({ groupLabel, groupDesc, cards }) => (
            <div key={groupLabel}>
              {/* Group label */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 rounded-full" style={{ background: orange.base }} />
                  <span className="text-sm font-semibold uppercase tracking-[0.15em] text-white/70">{groupLabel}</span>
                  <span className="text-white/30 text-sm hidden sm:inline">— {groupDesc}</span>
                </div>
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {cards.map(({ title, desc, img, alt }) => (
                  <div
                    key={title}
                    className="group rounded-sm overflow-hidden transition-all duration-300 border cursor-default"
                    style={{ background: navy.mid, borderColor: "rgba(255,255,255,0.06)" }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = `${orange.base}55`)}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.06)")}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden" style={{ background: navy.subtle }}>
                      <img src={img} alt={alt} width="600" height="400" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "Fraunces, serif" }}>{title}</h3>
                      <p className="text-white/40 text-sm leading-relaxed mb-5">{desc}</p>
                      <a href="#contact" className="inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all" style={{ color: orange.light }}>
                        Discuss This Service <ArrowRight size={13} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Repair, Refinish or Replace ──────────────────────────────────────────────
function ComparisonSection() {
  const decisions = [
    {
      title: "Repair",
      description: "Best when the damage is localized and the surrounding floor is still in good condition. Matching color, species, dimensions and finish may affect the final result.",
    },
    {
      title: "Refinish",
      description: "A strong option for eligible hardwood with worn finish, scratches, discoloration or an outdated appearance. The existing floor must be evaluated before sanding.",
    },
    {
      title: "Replace",
      description: "Recommended when the current material is no longer suitable, damage is widespread or a different floor better serves the home’s needs.",
    },
  ];

  return (
    <section id="assessment" className="py-24 lg:py-32" style={{ background: "#F8FAFC" }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-4" style={{ background: orange.tint }}>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.base }}>Repair · Refinish · Replace</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-semibold mb-4" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>
            What Does Your Floor Actually Need?
          </h2>
          <p className="text-[#64748B] text-lg max-w-2xl mx-auto">
            The best solution depends on where the problem is, the condition of the material and the result you expect.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {decisions.map(({ title, description }, index) => (
            <article key={title} className="bg-white border border-[#E4EAF0] rounded-sm p-7 shadow-sm">
              <div className="w-11 h-11 rounded-sm flex items-center justify-center mb-6 text-sm font-bold" style={{ background: orange.tint, color: orange.base }}>
                0{index + 1}
              </div>
              <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>{title}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{description}</p>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <a href="#contact" className="inline-flex items-center gap-3 px-9 py-4 text-white font-semibold rounded-sm transition-colors" style={{ background: orange.base }} onMouseEnter={(e) => (e.currentTarget.style.background = orange.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = orange.base)}>
            Schedule a Floor Assessment <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Service Process ──────────────────────────────────────────────────────────
function ServiceProcessSection() {
  const steps = [
    { num: "01", icon: MessageCircle, title: "Initial Conversation", desc: "Tell us what is happening with your floor, what result you want and what concerns you have about the project." },
    { num: "02", icon: Search, title: "On-Site Assessment", desc: "We evaluate the existing floor, subfloor conditions, moisture risks, transitions, room use and other factors that may affect the solution." },
    { num: "03", icon: Layers, title: "Options & Estimate", desc: "You receive a clear recommendation, project scope and estimate, including alternatives when more than one path makes sense." },
    { num: "04", icon: Wrench, title: "Preparation & Service", desc: "We confirm materials, preparation, schedule and access before completing the work with attention to long-term performance." },
    { num: "05", icon: CheckCircle, title: "Final Walkthrough", desc: "We review the finished work together, answer care questions and confirm the project before final completion." },
  ];

  const serviceTypes = ["Installation", "Refinishing", "Repairs"];

  return (
    <section id="process" className="py-24 lg:py-32" style={{ background: navy.mid }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-4 border" style={{ background: orange.tintLg, borderColor: `${orange.base}40` }}>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.light }}>Our Process</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-4" style={{ fontFamily: "Fraunces, serif" }}>
            A Clear Process From Assessment to Final Walkthrough
          </h2>
          <p className="text-white/40 text-lg max-w-xl mx-auto mb-5">
            You should understand what will happen, how to prepare and what to expect throughout the project.
          </p>
          {/* Service type pills */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {serviceTypes.map((s) => (
              <span key={s} className="px-3 py-1 rounded-sm text-xs font-medium border" style={{ color: orange.light, borderColor: `${orange.base}35`, background: orange.tint }}>
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-[3.25rem] left-[10%] right-[10%] h-px" style={{ background: `linear-gradient(to right, transparent, ${orange.base}30, transparent)` }} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {steps.map(({ num, icon: Icon, title, desc }) => (
              <div key={num} className="flex flex-col items-center text-center lg:items-start lg:text-left">
                <div
                  className="relative z-10 w-[4.5rem] h-[4.5rem] rounded-sm flex flex-col items-center justify-center mb-6 transition-all cursor-default border"
                  style={{ background: navy.light, borderColor: `${orange.base}35` }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = orange.base)}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = `${orange.base}35`)}
                >
                  <span className="text-[9px] font-mono tracking-[0.2em] mb-1 text-white/25">{num}</span>
                  <Icon size={22} style={{ color: orange.base }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Fraunces, serif" }}>{title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-14">
          <a href="#contact" className="inline-flex items-center gap-3 px-9 py-4 text-white font-semibold rounded-sm transition-colors" style={{ background: orange.base }} onMouseEnter={(e) => (e.currentTarget.style.background = orange.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = orange.base)}>
            Start With an Assessment <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Common Problems ──────────────────────────────────────────────────────────
function CommonProblemsSection() {
  const problems = [
    { icon: Hammer, title: "Scratches & Loss of Shine", desc: "Your floor may need a localized repair, screen and recoat or full refinishing. The depth and location of the damage determine the path." },
    { icon: Droplets, title: "Water-Related Damage", desc: "Moisture can affect the finish, flooring material or subfloor. We assess where the damage occurred before recommending a repair or replacement." },
    { icon: Volume2, title: "Uneven or Noisy Floors", desc: "Movement, squeaks and uneven areas may be related to the subfloor, installation method or surrounding conditions." },
    { icon: Palette, title: "Matching Existing Hardwood", desc: "We evaluate species, dimensions, grade, color and finish to determine how closely new material can blend with the existing floor." },
    { icon: AlertTriangle, title: "Pet Stains & Damage", desc: "Pet-related stains, scratches and odors require different approaches depending on how deeply they have affected the floor." },
    { icon: Compass, title: "Choosing a New Floor", desc: "Compare hardwood, engineered hardwood, LVP and laminate according to moisture, traffic, pets, maintenance, appearance and budget." },
  ];

  return (
    <section className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-4" style={{ background: orange.tint }}>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.base }}>Common Homeowner Challenges</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-semibold mb-4" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>
            Start With the Problem — Not the Product
          </h2>
          <p className="text-[#64748B] text-lg max-w-2xl mx-auto">
            You do not need to know which service to request. Tell us what you are seeing, and we will help identify the possible causes and solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {problems.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="flex gap-4 p-6 rounded-sm transition-all border cursor-default hover:shadow-md"
              style={{ background: "white", borderColor: "#E4EAF0", borderLeftWidth: "1px" }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = `${orange.base}45`;
                el.style.borderLeftColor = orange.base;
                el.style.borderLeftWidth = "3px";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = "#E4EAF0";
                el.style.borderLeftColor = "#E4EAF0";
                el.style.borderLeftWidth = "1px";
              }}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-sm flex items-center justify-center mt-0.5" style={{ background: orange.tint }}>
                <Icon size={17} style={{ color: orange.base }} />
              </div>
              <div>
                <h3 className="font-semibold mb-1.5" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>{title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-3 px-9 py-4 font-semibold rounded-sm transition-colors border-2"
            style={{ color: navy.base, borderColor: navy.base }}
            onMouseEnter={(e) => { e.currentTarget.style.background = navy.base; e.currentTarget.style.color = "white"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = navy.base; }}
          >
            Ask Your Question <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Educational Content ──────────────────────────────────────────────────────
function EducationalContentSection() {
  const articles = [
    {
      title:    "Does My Floor Need Repair, Refinishing or Replacement?",
      excerpt:  "A practical starting point for understanding what kind of intervention your floor may need.",
      img:      "/assets/article-vinyl-laminate.jpg",
      alt:      "Close up wood grain texture",
      tag:      "Assessment",
      readTime: "Guide",
    },
    {
      title:    "Hardwood or LVP: Which Makes Sense for Your Home?",
      excerpt:  "Compare appearance, moisture, pets, maintenance, comfort and long-term value.",
      img:      "/assets/article-hardwood.jpg",
      alt:      "Elegant living room with hardwood flooring and fireplace",
      tag:      "Flooring Types",
      readTime: "Guide",
    },
    {
      title:    "What Should Be Included in a Flooring Estimate?",
      excerpt:  "Learn how to compare scope, preparation, materials and warranties — not only the final price.",
      img:      "/assets/article-choose-floor.jpg",
      alt:      "Minimalist modern living room with wood sideboard",
      tag:      "Planning",
      readTime: "Guide",
    },
  ];

  return (
    <section id="blog" className="py-24 lg:py-32" style={{ background: navy.base }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-4 border" style={{ background: orange.tintLg, borderColor: `${orange.base}40` }}>
              <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.light }}>Educational Resources</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-white leading-tight" style={{ fontFamily: "Fraunces, serif" }}>Understand Your Floor Before You Decide</h2>
            <p className="text-white/40 text-lg max-w-2xl mt-5">Clear answers about materials, repairs, refinishing, installation and maintenance — without unnecessary technical language.</p>
          </div>
          <a href="#" className="text-sm font-medium flex items-center gap-2 transition-all self-start lg:self-end hover:gap-3" style={{ color: orange.light }}>
            View All Articles <ArrowRight size={13} />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {articles.map(({ title, excerpt, img, alt, tag, readTime }) => (
            <div
              key={title}
              className="group rounded-sm overflow-hidden transition-all border cursor-default"
              style={{ background: navy.mid, borderColor: "rgba(255,255,255,0.06)" }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = `${orange.base}55`)}
              onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.06)")}
            >
              <div className="relative aspect-[16/10] overflow-hidden" style={{ background: navy.subtle }}>
                <img src={img} alt={alt} width="800" height="500" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${navy.mid}60, transparent)` }} />
                <div className="absolute top-4 left-4 px-2.5 py-1 text-white text-xs font-semibold rounded-sm" style={{ background: orange.base }}>{tag}</div>
              </div>
              <div className="p-6">
                <div className="text-white/30 text-xs mb-3 font-mono">{readTime}</div>
                <h3 className="text-xl font-semibold text-white mb-3 leading-snug" style={{ fontFamily: "Fraunces, serif" }}>{title}</h3>
                <p className="text-white/40 text-sm leading-relaxed mb-5">{excerpt}</p>
                <a href="#" className="inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all" style={{ color: orange.light }}>
                  Read More <ArrowRight size={13} />
                </a>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    { q: "Do I need to repair, refinish or replace my floor?", a: "It depends on where the damage is and how deeply it affects the floor. Surface wear or loss of shine may be addressed with a screen and recoat. Eligible hardwood with deeper scratches or discoloration may need refinishing. Localized damage may be repairable, while widespread material or subfloor problems can make replacement the better option. An on-site assessment is the safest way to compare these paths." },
  ];

  return (
    <section id="faq" className="py-24 lg:py-32" style={{ background: "#F8FAFC" }}>
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-4" style={{ background: orange.tint }}>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.base }}>Frequently Asked Questions</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-semibold" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>
            Common Questions Answered
          </h2>
        </div>

        <div className="space-y-3 mb-14">
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className="rounded-sm overflow-hidden border transition-all"
                style={{
                  background:  "white",
                  borderColor: isOpen ? `${orange.base}45` : "#E4EAF0",
                  boxShadow:   isOpen ? "0 2px 16px rgba(217,95,14,0.08)" : "none",
                }}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-7 py-6 text-left"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                >
                  <span className="font-semibold text-lg leading-snug" style={{ fontFamily: "Fraunces, serif", color: navy.base }}>{faq.q}</span>
                  <div className="flex-shrink-0 w-8 h-8 rounded-sm flex items-center justify-center transition-colors" style={{ background: isOpen ? orange.base : "#EFF3F7", color: isOpen ? "white" : navy.base }}>
                    {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </div>
                </button>
                {isOpen && (
                  <div id={`faq-panel-${i}`} className="px-7 pb-7">
                    <p className="text-[#64748B] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <a
            href="#"
            className="inline-flex items-center gap-3 px-12 py-5 font-semibold text-lg rounded-sm transition-colors border-2"
            style={{ color: navy.base, borderColor: navy.base }}
            onMouseEnter={(e) => { e.currentTarget.style.background = navy.base; e.currentTarget.style.color = "white"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = navy.base; }}
          >
            View All Questions <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Service Area ──────────────────────────────────────────────────────────────
function ConfirmedServiceAreaSection() {
  return (
    <section className="py-24 lg:py-32" style={{ background: navy.base }}>
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-6 border" style={{ background: orange.tintLg, borderColor: `${orange.base}40` }}>
          <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.light }}>Service Area</span>
        </div>
        <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-6 leading-tight" style={{ fontFamily: "Fraunces, serif" }}>
          Residential Flooring Services in the Denver Metro Area
        </h2>
        <p className="text-white/45 text-lg leading-relaxed max-w-3xl mx-auto mb-10">
          VC Flooring serves homeowners throughout the Denver metro area and selected surrounding communities. Contact us with your address and project details to confirm availability.
        </p>
        <a href="mailto:contact@vcflooringco.com" onClick={() => trackEvent("email_click", { placement: "service_area" })} className="inline-flex items-center gap-3 px-8 py-4 text-white font-semibold rounded-sm transition-colors" style={{ background: orange.base }} onMouseEnter={(e) => (e.currentTarget.style.background = orange.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = orange.base)}>
          Check Service Availability <ArrowRight size={15} />
        </a>
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────────────
function FinalCTASection() {
  return (
    <section id="contact" className="relative py-32 lg:py-44 overflow-hidden" style={{ background: navy.mid }}>
      {/* Background texture: subtle image overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="/assets/cta-background.jpg"
          alt=""
          width="1920"
          height="800"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
          style={{ opacity: 0.06 }}
        />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, ${orange.base}14 0%, transparent 65%)` }} />
      </div>

      {/* Left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: orange.base }} />

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-8 border" style={{ background: orange.tintLg, borderColor: `${orange.base}40` }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: orange.base }} />
          <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: orange.light }}>Start With a Conversation</span>
        </div>

        <h2 className="text-6xl lg:text-7xl font-semibold text-white mb-7 leading-[1.02]" style={{ fontFamily: "Fraunces, serif" }}>
          Not Sure What<br />Your Floor Needs?
        </h2>
        <p className="text-white/50 text-xl mb-14 max-w-2xl mx-auto leading-relaxed">
          Tell us what is happening, what result you want and where your project is located. We will help you understand the next step — whether that means repairing, refinishing or replacing your floor.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="mailto:contact@vcflooringco.com"
            onClick={() => trackEvent("email_click", { placement: "final_cta" })}
            className="px-12 py-5 text-white font-semibold text-lg rounded-sm transition-colors"
            style={{ background: orange.base }}
            onMouseEnter={(e) => (e.currentTarget.style.background = orange.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = orange.base)}
          >
            Request an On-Site Estimate
          </a>
          <a
            href="#"
            className="px-12 py-5 border-2 text-white font-semibold text-lg rounded-sm transition-colors flex items-center justify-center gap-3 hover:bg-white/8"
            style={{ borderColor: "rgba(255,255,255,0.28)" }}
          >
            <Phone size={18} />Call Victor
          </a>
        </div>
        <p className="text-white/25 text-sm mt-8">Clear recommendations · Detailed project scope · No-pressure guidance</p>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const quickLinks = [
    { label: "Home",           href: "#top" },
    { label: "Services",       href: "#services" },
    { label: "Process",        href: "#process" },
    { label: "Floor Assessment", href: "#assessment" },
    { label: "FAQ",            href: "#faq" },
    { label: "Contact",        href: "#contact" },
  ];
  const services = ["Floor Installation", "Hardwood Refinishing", "Floor Repairs", "Screen & Recoat", "Floor Assessments"];

  const trustBadges = [
    { label: "Direct Communication", sub: "From first conversation", bold: true },
    { label: "Clear Project Scope", sub: "Options and estimate" },
    { label: "Professional Preparation", sub: "Conditions considered" },
    { label: "Final Walkthrough", sub: "Care guidance included", bold: true },
  ];

  return (
    <footer className="border-t" style={{ background: navy.deepest, borderColor: "rgba(255,255,255,0.05)" }}>
      {/* Trust badge strip */}
      <div className="border-b" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
            {trustBadges.map(({ label, sub, bold }, i) => (
              <div key={label} className="flex items-center gap-3">
                {i > 0 && <div className="hidden sm:block w-px h-7 self-center" style={{ background: "rgba(255,255,255,0.07)" }} />}
                <div className="flex items-center gap-2.5 border rounded-sm px-4 py-2" style={{ borderColor: `${orange.base}30`, background: orange.tint }}>
                  <CheckCircle size={13} style={{ color: orange.base }} />
                  <div>
                    <div className={`text-xs leading-none ${bold ? "font-semibold" : "font-medium"}`} style={{ color: "rgba(255,255,255,0.75)" }}>{label}</div>
                    <div className="text-[10px] leading-none mt-0.5" style={{ color: orange.light }}>{sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 mb-14">
          <div className="col-span-2 lg:col-span-4">
            <img
              src="/assets/vc-flooring-logo-white.png"
              alt="VC Flooring"
              width="616"
              height="392"
              loading="lazy"
              className="h-24 w-auto mb-5"
            />
            <p className="text-white/30 text-sm leading-relaxed mb-6 max-w-xs">
              Expert guidance, installation, refinishing and repair for hardwood, vinyl and laminate floors in the Denver metro area.
            </p>
            <div className="flex gap-2.5">
              {[
                { Icon: Instagram, label: "Instagram" },
                { Icon: Facebook, label: "Facebook" },
                { Icon: Youtube, label: "YouTube" },
              ].map(({ Icon, label }) => (
                <a key={label} href="#" aria-label={`${label} link pending`} className="w-9 h-9 rounded-sm flex items-center justify-center transition-colors group" style={{ background: "rgba(255,255,255,0.05)" }} onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.background = orange.tintLg)} onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.05)")}>
                  <Icon size={15} className="text-white/35 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <h2 className="text-white/35 text-[9px] uppercase tracking-[0.2em] mb-5">Quick Links</h2>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-white/35 text-sm transition-colors hover:text-white">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-3">
            <h2 className="text-white/35 text-[9px] uppercase tracking-[0.2em] mb-5">Services</h2>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s}><a href="#" className="text-white/35 text-sm transition-colors hover:text-white">{s}</a></li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-3">
            <h2 className="text-white/35 text-[9px] uppercase tracking-[0.2em] mb-5">Contact</h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3"><Mail size={13} className="mt-0.5 flex-shrink-0" style={{ color: orange.base }} /><a href="mailto:contact@vcflooringco.com" onClick={() => trackEvent("email_click", { placement: "footer" })} className="text-white/40 text-sm hover:text-white">contact@vcflooringco.com</a></li>
              <li className="flex items-start gap-3"><MapPin size={13} className="mt-0.5 flex-shrink-0" style={{ color: orange.base }} /><span className="text-white/40 text-sm leading-relaxed">Denver Metro Area, CO<br />Contact us to confirm availability.</span></li>
            </ul>
          </div>
        </div>

        <div className="border-t pt-7 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
          <p className="text-white/18 text-xs">© 2026 VC Flooring. All rights reserved.</p>
          <div className="flex gap-6">
            {["Privacy Policy", "Terms of Service"].map((label) => (
              <a key={label} href="#" className="text-white/18 text-xs transition-colors hover:text-white/45">{label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const pendingLinks = Array.from(
      document.querySelectorAll<HTMLAnchorElement>('a[href="#"]'),
    );
    const preventPendingNavigation = (event: Event) => event.preventDefault();

    pendingLinks.forEach((link) => {
      link.setAttribute("aria-disabled", "true");
      link.setAttribute("title", "Destination pending confirmation");
      link.addEventListener("click", preventPendingNavigation);
    });

    return () => {
      pendingLinks.forEach((link) => {
        link.removeEventListener("click", preventPendingNavigation);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "DM Sans, sans-serif" }}>
      <a href="#main-content" className="fixed left-4 top-4 z-[60] -translate-y-24 bg-white px-4 py-3 font-semibold text-[#061B2D] shadow-lg focus:translate-y-0">
        Skip to main content
      </a>
      <Header scrolled={scrolled} />
      <main id="main-content">
        <HeroSection />
        <TrustStrip />
        <WhyVictorSection />
        <FlooringTypesSection />
        <ComparisonSection />
        <ServiceProcessSection />
        <CommonProblemsSection />
        <EducationalContentSection />
        <FAQSection />
        <ConfirmedServiceAreaSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
}
